import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { confirmForgotPassword, forgotPassword } from '@/api/auth';
import { ApiError } from '@/api/client';
import { AmbientBackground } from '@/components/layout/AmbientBackground';
import { Button } from '@/components/ui/Button';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { AuthSiteBar } from '@/components/layout/AuthSiteBar';
import { Spinner } from '@/components/ui/Spinner';
import { useI18n } from '@/i18n';

type Step = 'request' | 'confirm' | 'done';

export function ForgotPasswordPage() {
  const { t } = useI18n();
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      setStep('confirm');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('forgot.sendError'));
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await confirmForgotPassword({
        email: email.trim(),
        confirmationCode: code.trim(),
        password,
      });
      setStep('done');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('forgot.updateError'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <AmbientBackground />
      <div className="auth-page__frame">
        <div className="auth-card glass-panel">
          <p className="auth-card__eyebrow">{t('forgot.eyebrow')}</p>
          <h1 className="auth-card__title display">{t('forgot.title')}</h1>

          {step === 'request' ? (
            <form className="auth-form" onSubmit={handleRequest}>
              <p className="auth-card__subtitle">{t('forgot.requestSubtitle')}</p>
              <label className="field">
                <span className="field__label">{t('auth.email')}</span>
                <input
                  className="contact-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              {error ? <ErrorMessage message={error} /> : null}
              <Button type="submit" className="btn-primary auth-form__submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : t('forgot.sendCode')}
              </Button>
            </form>
          ) : null}

          {step === 'confirm' ? (
            <form className="auth-form" onSubmit={handleConfirm}>
              <p className="auth-card__subtitle">{t('forgot.confirmSubtitle')}</p>
              <label className="field">
                <span className="field__label">{t('forgot.code')}</span>
                <input
                  className="contact-input"
                  type="text"
                  inputMode="numeric"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field__label">{t('forgot.newPassword')}</span>
                <input
                  className="contact-input"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              {error ? <ErrorMessage message={error} /> : null}
              <Button type="submit" className="btn-primary auth-form__submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : t('forgot.update')}
              </Button>
            </form>
          ) : null}

          {step === 'done' ? (
            <div className="auth-form">
              <p className="auth-card__subtitle">{t('forgot.doneSubtitle')}</p>
              <Link to="/signin">
                <Button className="btn-primary auth-form__submit">
                  {t('forgot.backToSignIn')}
                </Button>
              </Link>
            </div>
          ) : null}

          <p className="auth-card__footer">
            <Link to="/signin" className="auth-link">
              ← {t('forgot.backToSignIn')}
            </Link>
          </p>
        </div>
        <AuthSiteBar />
      </div>
    </div>
  );
}
